import React, { useState, useEffect, useMemo } from 'react';
import {
  RefreshCw,
  AlertTriangle,
  BarChart3,
  Users,
  Scale,
  ShoppingCart,
  DollarSign,
  Search,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Select, Checkbox } from '@components/Input';
import { Card, CardContent } from '@components/Card';
import { SimpleTable as Table, TableHeader, TableRow, TableCell } from '@components/Table';
import { Tabs } from '@components/Tabs';
import { DateInputBR } from '@components/DateInputBR';
import { productionService } from '@services/productionService';
import {
  ProductionActivity,
  ProductionEmployee,
  ProductivityRow,
  MaterialBalanceRow,
  MaterialRequirementsResult,
  OrderCostRow,
  getActivityCategoryLabel,
} from '@/types/production';
import { MATERIAL_TYPE_OPTIONS } from '@/types/rawMaterial';
import { clsx } from 'clsx';

// ============================================================
// Helpers
// ============================================================

/** Data em AAAA-MM-DD no fuso local (sem shift), deslocada em `offsetDays` dias */
const localISO = (offsetDays = 0): string => {
  const d = new Date();
  d.setDate(d.getDate() + offsetDays);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate()
  ).padStart(2, '0')}`;
};

/** Formata data ISO para dd/mm/aaaa sem deslocamento de fuso (parse manual) */
const formatDate = (value?: string): string => {
  if (!value) return '-';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) {
    return `${match[3]}/${match[2]}/${match[1]}`;
  }
  return '-';
};

/** Formata minutos como 'Xh Ymin' quando > 60min */
const formatMinutes = (minutes?: number | null): string => {
  if (minutes === undefined || minutes === null) return '-';
  const total = Math.round(minutes);
  if (total > 60) {
    return `${Math.floor(total / 60)}h ${total % 60}min`;
  }
  return `${total}min`;
};

const formatNumber = (value?: number | null, maxDigits = 2): string => {
  if (value === undefined || value === null) return '-';
  return value.toLocaleString('pt-BR', { maximumFractionDigits: maxDigits });
};

const formatCurrency = (value?: number | null): string => {
  if (value === undefined || value === null) return '-';
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatPercent = (value?: number | null): string => {
  if (value === undefined || value === null) return '-';
  return `${value.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
};

const getMaterialTypeLabel = (materialType?: number | null): string => {
  const option = MATERIAL_TYPE_OPTIONS.find((o) => o.value === materialType);
  return option ? option.label : '-';
};

const MATERIAL_TYPE_BADGE_CLASS: Record<number, string> = {
  0: 'bg-blue-100 text-blue-800',
  1: 'bg-slate-100 text-slate-700',
  2: 'bg-purple-100 text-purple-800',
  3: 'bg-amber-100 text-amber-800',
};

const MaterialTypeBadge: React.FC<{ materialType?: number | null }> = ({ materialType }) => (
  <span
    className={clsx(
      'inline-flex px-2 py-0.5 text-xs font-medium rounded-full',
      MATERIAL_TYPE_BADGE_CLASS[materialType ?? -1] || 'bg-slate-100 text-slate-700'
    )}
  >
    {getMaterialTypeLabel(materialType)}
  </span>
);

const CATEGORY_BADGE_CLASS: Record<number, string> = {
  0: 'bg-slate-100 text-slate-700',
  1: 'bg-purple-100 text-purple-800',
  2: 'bg-amber-100 text-amber-800',
  3: 'bg-blue-100 text-blue-800',
  4: 'bg-emerald-100 text-emerald-800',
  5: 'bg-pink-100 text-pink-800',
};

const CategoryBadge: React.FC<{ category: number }> = ({ category }) => (
  <span
    className={clsx(
      'inline-flex px-2 py-0.5 text-xs font-medium rounded-full',
      CATEGORY_BADGE_CLASS[category] || 'bg-slate-100 text-slate-700'
    )}
  >
    {getActivityCategoryLabel(category)}
  </span>
);

/** Eficiência: verde > 100%, vermelho < 80%, neutro entre 80 e 100 */
const efficiencyClass = (value?: number | null): string => {
  if (value === undefined || value === null) return 'text-slate-400';
  if (value > 100) return 'text-emerald-700 font-semibold';
  if (value < 80) return 'text-red-600 font-semibold';
  return 'text-slate-700';
};

const ErrorBanner: React.FC<{ message: string }> = ({ message }) => (
  <Card className="bg-red-50 border-red-200">
    <CardContent>
      <div className="flex items-center gap-2">
        <AlertTriangle className="text-red-600" size={18} />
        <p className="text-red-600">{message}</p>
      </div>
    </CardContent>
  </Card>
);

const LoadingState: React.FC<{ message: string }> = ({ message }) => (
  <div className="text-center py-8">
    <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
    <p className="text-slate-500">{message}</p>
  </div>
);

const EmptyState: React.FC<{ message: string }> = ({ message }) => (
  <div className="text-center py-8">
    <BarChart3 className="w-12 h-12 text-slate-400 mx-auto mb-4" />
    <p className="text-slate-500">{message}</p>
  </div>
);

interface PeriodFilterFieldsProps {
  dateFrom: string;
  dateTo: string;
  onDateFrom: (v: string) => void;
  onDateTo: (v: string) => void;
}

const PeriodFilterFields: React.FC<PeriodFilterFieldsProps> = ({
  dateFrom,
  dateTo,
  onDateFrom,
  onDateTo,
}) => (
  <>
    <div>
      <label className="text-label block mb-1">Data inicial</label>
      <DateInputBR className="input w-full" value={dateFrom} onChange={onDateFrom} />
    </div>
    <div>
      <label className="text-label block mb-1">Data final</label>
      <DateInputBR className="input w-full" value={dateTo} onChange={onDateTo} />
    </div>
  </>
);

// ============================================================
// Aba: Produtividade
// ============================================================

interface ProductivitySubtotal {
  entries: number;
  quantityOk: number;
  quantityDefect: number;
  timeSpent: number;
  defectPercent: number;
  piecesPerHour?: number;
  efficiencyPercent?: number;
}

const computeSubtotal = (rows: ProductivityRow[]): ProductivitySubtotal => {
  const entries = rows.reduce((sum, r) => sum + (r.entries || 0), 0);
  const quantityOk = rows.reduce((sum, r) => sum + (r.quantity_ok || 0), 0);
  const quantityDefect = rows.reduce((sum, r) => sum + (r.quantity_defect || 0), 0);
  const timeSpent = rows.reduce((sum, r) => sum + (r.time_spent_minutes || 0), 0);
  const estimated = rows.reduce((sum, r) => sum + (r.estimated_minutes || 0), 0);
  const totalPieces = quantityOk + quantityDefect;

  return {
    entries,
    quantityOk,
    quantityDefect,
    timeSpent,
    defectPercent: totalPieces > 0 ? (quantityDefect / totalPieces) * 100 : 0,
    piecesPerHour: timeSpent > 0 ? (quantityOk * 60) / timeSpent : undefined,
    efficiencyPercent: timeSpent > 0 && estimated > 0 ? (estimated / timeSpent) * 100 : undefined,
  };
};

const ProductivityTab: React.FC = () => {
  const [dateFrom, setDateFrom] = useState(() => localISO(-30));
  const [dateTo, setDateTo] = useState(() => localISO());
  const [employeeFilter, setEmployeeFilter] = useState('');
  const [activityFilter, setActivityFilter] = useState('');

  const [employees, setEmployees] = useState<ProductionEmployee[]>([]);
  const [activities, setActivities] = useState<ProductionActivity[]>([]);

  const [rows, setRows] = useState<ProductivityRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [employeesResult, activitiesResult] = await Promise.all([
          productionService.listEmployees({ page: 1, pageSize: 100 }),
          productionService.listActivities({ page: 1, pageSize: 100 }),
        ]);
        setEmployees(employeesResult.data);
        setActivities(activitiesResult.data);
      } catch {
        // Falha silenciosa nos selects — o relatório exibirá erros se houver
      }
    };
    loadOptions();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await productionService.getProductivityReport({
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        employee_id: employeeFilter ? parseInt(employeeFilter) : undefined,
        activity_id: activityFilter ? parseInt(activityFilter) : undefined,
      });
      setRows(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar relatório de produtividade');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo, employeeFilter, activityFilter]);

  // Agrupa as linhas por colaborador preservando a ordem retornada
  const groups = useMemo(() => {
    const map = new Map<number, { employeeName: string; rows: ProductivityRow[] }>();
    for (const row of rows) {
      const group = map.get(row.employee_id);
      if (group) {
        group.rows.push(row);
      } else {
        map.set(row.employee_id, { employeeName: row.employee_name, rows: [row] });
      }
    }
    return Array.from(map.entries()).map(([employeeId, group]) => ({ employeeId, ...group }));
  }, [rows]);

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <Card>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
            <PeriodFilterFields
              dateFrom={dateFrom}
              dateTo={dateTo}
              onDateFrom={setDateFrom}
              onDateTo={setDateTo}
            />
            <Select
              label="Colaborador"
              value={employeeFilter}
              onChange={(e) => setEmployeeFilter(e.target.value)}
            >
              <option value="">Todos</option>
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.name}
                </option>
              ))}
            </Select>
            <Select
              label="Atividade"
              value={activityFilter}
              onChange={(e) => setActivityFilter(e.target.value)}
            >
              <option value="">Todas</option>
              {activities.map((activity) => (
                <option key={activity.id} value={activity.id}>
                  {activity.code} - {activity.name}
                </option>
              ))}
            </Select>
            <div>
              <Button
                variant="outline"
                onClick={loadData}
                loading={loading}
                icon={<RefreshCw size={16} />}
              >
                Atualizar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && <ErrorBanner message={error} />}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <LoadingState message="Carregando produtividade..." />
          ) : groups.length === 0 ? (
            <EmptyState message="Nenhum apontamento encontrado no período" />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <tr>
                    <th className="text-left">Atividade</th>
                    <th className="text-left">Categoria</th>
                    <th className="text-right">Lançamentos</th>
                    <th className="text-right">OK</th>
                    <th className="text-right">Defeitos</th>
                    <th className="text-right">% Defeito</th>
                    <th className="text-right">Tempo</th>
                    <th className="text-right">Peças/hora</th>
                    <th className="text-right">Eficiência</th>
                  </tr>
                </TableHeader>
                <tbody>
                  {groups.map((group) => {
                    const subtotal = computeSubtotal(group.rows);
                    return (
                      <React.Fragment key={group.employeeId}>
                        {/* Cabeçalho do colaborador */}
                        <tr className="bg-slate-100">
                          <td colSpan={9} className="px-4 py-2">
                            <span className="font-semibold text-slate-800">
                              {group.employeeName}
                            </span>
                          </td>
                        </tr>
                        {group.rows.map((row) => (
                          <TableRow key={`${row.employee_id}-${row.activity_id}`}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">
                                  {row.activity_code}
                                </code>
                                <span className="text-sm text-slate-700">{row.activity_name}</span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <CategoryBadge category={row.activity_category} />
                            </TableCell>
                            <TableCell className="text-right">
                              {formatNumber(row.entries, 0)}
                            </TableCell>
                            <TableCell className="text-right">
                              <span className="font-medium text-emerald-700">
                                {formatNumber(row.quantity_ok, 0)}
                              </span>
                            </TableCell>
                            <TableCell className="text-right">
                              {row.quantity_defect ? (
                                <span className="font-medium text-red-600">
                                  {formatNumber(row.quantity_defect, 0)}
                                </span>
                              ) : (
                                <span className="text-slate-400">0</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatPercent(row.defect_percent)}
                            </TableCell>
                            <TableCell className="text-right">
                              {row.time_spent_minutes ? (
                                formatMinutes(row.time_spent_minutes)
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {formatNumber(row.pieces_per_hour, 1)}
                            </TableCell>
                            <TableCell className="text-right">
                              <span className={efficiencyClass(row.efficiency_percent)}>
                                {formatPercent(row.efficiency_percent)}
                              </span>
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Subtotal do colaborador */}
                        <tr className="bg-slate-50 border-t border-slate-200">
                          <td className="px-4 py-2 text-sm font-semibold text-slate-600" colSpan={2}>
                            Subtotal — {group.employeeName}
                          </td>
                          <td className="px-4 py-2 text-right text-sm font-semibold">
                            {formatNumber(subtotal.entries, 0)}
                          </td>
                          <td className="px-4 py-2 text-right text-sm font-semibold text-emerald-700">
                            {formatNumber(subtotal.quantityOk, 0)}
                          </td>
                          <td className="px-4 py-2 text-right text-sm font-semibold text-red-600">
                            {formatNumber(subtotal.quantityDefect, 0)}
                          </td>
                          <td className="px-4 py-2 text-right text-sm font-semibold">
                            {formatPercent(subtotal.defectPercent)}
                          </td>
                          <td className="px-4 py-2 text-right text-sm font-semibold">
                            {subtotal.timeSpent ? formatMinutes(subtotal.timeSpent) : '-'}
                          </td>
                          <td className="px-4 py-2 text-right text-sm font-semibold">
                            {formatNumber(subtotal.piecesPerHour, 1)}
                          </td>
                          <td className="px-4 py-2 text-right text-sm">
                            <span className={efficiencyClass(subtotal.efficiencyPercent)}>
                              {formatPercent(subtotal.efficiencyPercent)}
                            </span>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================================
// Aba: Balanço de Materiais
// ============================================================

const MaterialBalanceTab: React.FC = () => {
  const [dateFrom, setDateFrom] = useState(() => localISO(-30));
  const [dateTo, setDateTo] = useState(() => localISO());

  const [rows, setRows] = useState<MaterialBalanceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filtros client-side
  const [search, setSearch] = useState('');
  const [onlyDiscrepancy, setOnlyDiscrepancy] = useState(false);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await productionService.getMaterialBalanceReport({
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      setRows(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar balanço de materiais');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (onlyDiscrepancy && (row.discrepancy || 0) === 0) return false;
      if (!q) return true;
      return (
        (row.code || '').toLowerCase().includes(q) ||
        (row.description || '').toLowerCase().includes(q)
      );
    });
  }, [rows, search, onlyDiscrepancy]);

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <Card>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
            <PeriodFilterFields
              dateFrom={dateFrom}
              dateTo={dateTo}
              onDateFrom={setDateFrom}
              onDateTo={setDateTo}
            />
            <div>
              <label className="text-label block mb-1">Buscar material</label>
              <div className="relative">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                  size={14}
                />
                <input
                  type="text"
                  className="input w-full pl-8"
                  placeholder="Código ou descrição..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </div>
            <div className="pb-2">
              <Checkbox
                label="Somente com discrepância"
                checked={onlyDiscrepancy}
                onChange={(e) => setOnlyDiscrepancy(e.target.checked)}
              />
            </div>
            <div>
              <Button
                variant="outline"
                onClick={loadData}
                loading={loading}
                icon={<RefreshCw size={16} />}
              >
                Atualizar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && <ErrorBanner message={error} />}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <LoadingState message="Carregando balanço de materiais..." />
          ) : filteredRows.length === 0 ? (
            <EmptyState
              message={
                rows.length === 0
                  ? 'Nenhum material encontrado no período'
                  : 'Nenhum material corresponde aos filtros'
              }
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <tr>
                    <th className="text-left">Código</th>
                    <th className="text-left">Descrição</th>
                    <th className="text-left">Tipo</th>
                    <th className="text-left">Un.</th>
                    <th className="text-right">Estoque inicial</th>
                    <th className="text-right">Recebido</th>
                    <th className="text-right">Entrada produção</th>
                    <th className="text-right">Consumo produção</th>
                    <th className="text-right">Perdas</th>
                    <th className="text-right">Vendas</th>
                    <th className="text-right">Ajustes</th>
                    <th className="text-right">Teórico final</th>
                    <th className="text-right">Registrado final</th>
                    <th className="text-right">Discrepância</th>
                    <th className="text-right">Estoque atual</th>
                  </tr>
                </TableHeader>
                <tbody>
                  {filteredRows.map((row) => {
                    const adjustments =
                      (row.qty_adjustments_in || 0) - (row.qty_adjustments_out || 0);
                    const hasDiscrepancy = (row.discrepancy || 0) !== 0;
                    return (
                      <TableRow key={row.raw_material_id}>
                        <TableCell>
                          <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">
                            {row.code}
                          </code>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-700">{row.description}</span>
                        </TableCell>
                        <TableCell>
                          <MaterialTypeBadge materialType={row.material_type} />
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-slate-500">{row.unit || '-'}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(row.initial_stock)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(row.qty_received)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(row.qty_production_entries)}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(row.qty_consumed_production)}
                        </TableCell>
                        <TableCell className="text-right">
                          {row.qty_lost_production ? (
                            <span className="text-red-600">
                              {formatNumber(row.qty_lost_production)}
                            </span>
                          ) : (
                            <span className="text-slate-400">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(row.qty_sales_out)}
                        </TableCell>
                        <TableCell className="text-right">
                          <span
                            title={`Entradas: ${formatNumber(
                              row.qty_adjustments_in
                            )} | Saídas: ${formatNumber(row.qty_adjustments_out)}`}
                          >
                            {adjustments > 0 ? '+' : ''}
                            {formatNumber(adjustments)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {formatNumber(row.theoretical_end)}
                        </TableCell>
                        <TableCell className="text-right">{formatNumber(row.end_stock)}</TableCell>
                        <TableCell className="text-right">
                          <span
                            title={
                              hasDiscrepancy
                                ? 'fluxos físicos não explicam o estoque registrado'
                                : undefined
                            }
                            className={clsx(
                              hasDiscrepancy ? 'text-red-600 font-semibold' : 'text-slate-400'
                            )}
                          >
                            {(row.discrepancy || 0) > 0 ? '+' : ''}
                            {formatNumber(row.discrepancy)}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <span className="font-medium text-slate-900">
                            {formatNumber(row.current_stock)}
                          </span>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================================
// Aba: Necessidade de Compra
// ============================================================

type MaterialActionFilter = 'all' | 'buy' | 'produce';

const MaterialRequirementsTab: React.FC = () => {
  const [result, setResult] = useState<MaterialRequirementsResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionFilter, setActionFilter] = useState<MaterialActionFilter>('all');

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await productionService.getMaterialRequirements();
      setResult(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar necessidade de materiais');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const products = result?.products ?? [];
  const materials = result?.materials ?? [];
  const missingBom = result?.missing_bom_products ?? [];

  const filteredMaterials = useMemo(() => {
    if (actionFilter === 'buy') return materials.filter((m) => !m.has_bom);
    if (actionFilter === 'produce') return materials.filter((m) => m.has_bom);
    return materials;
  }, [materials, actionFilter]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <p className="text-sm text-slate-600">
          Demanda calculada a partir dos pedidos de venda em digitação (sem filtro de período)
        </p>
        <Button
          variant="outline"
          onClick={loadData}
          loading={loading}
          icon={<RefreshCw size={16} />}
        >
          Atualizar
        </Button>
      </div>

      {error && <ErrorBanner message={error} />}

      {loading ? (
        <Card>
          <CardContent className="p-0">
            <LoadingState message="Calculando necessidades..." />
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Alerta de produtos sem BOM */}
          {missingBom.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertTriangle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium mb-1">
                    Produtos com demanda sem Lista de Materiais (BOM):
                  </p>
                  <p>{missingBom.join(', ')}</p>
                  <p className="text-xs text-amber-700 mt-1">
                    A necessidade de materiais destes produtos não está incluída no cálculo.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Seção 1: Produtos a produzir */}
          <Card>
            <div className="p-4 border-b border-slate-200">
              <h3 className="text-base font-semibold text-slate-900">Produtos a produzir</h3>
            </div>
            <CardContent className="p-0">
              {products.length === 0 ? (
                <EmptyState message="Nenhum produto com demanda pendente" />
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <tr>
                        <th className="text-left">SKU</th>
                        <th className="text-left">Descrição</th>
                        <th className="text-right">Demanda</th>
                        <th className="text-right">Estoque</th>
                        <th className="text-right">Líquido a produzir</th>
                        <th className="text-center">BOM</th>
                      </tr>
                    </TableHeader>
                    <tbody>
                      {products.map((product) => (
                        <TableRow key={product.product_id}>
                          <TableCell>
                            <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">
                              {product.sku}
                            </code>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-slate-700">
                              {product.description || '-'}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            {formatNumber(product.demand)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatNumber(product.stock_available)}
                          </TableCell>
                          <TableCell className="text-right">
                            <span
                              className={clsx(
                                product.net_to_produce > 0
                                  ? 'font-semibold text-slate-900'
                                  : 'text-slate-400'
                              )}
                            >
                              {formatNumber(product.net_to_produce)}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            {product.has_bom ? (
                              <span className="text-slate-400 text-sm">OK</span>
                            ) : (
                              <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-red-100 text-red-700">
                                Sem BOM
                              </span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Seção 2: Materiais */}
          <Card>
            <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h3 className="text-base font-semibold text-slate-900">Materiais</h3>
              <div className="flex items-center gap-1 rounded-lg border border-slate-200 p-1">
                {(
                  [
                    { value: 'all', label: 'Todos' },
                    { value: 'buy', label: 'Comprar' },
                    { value: 'produce', label: 'Produzir' },
                  ] as { value: MaterialActionFilter; label: string }[]
                ).map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setActionFilter(option.value)}
                    className={clsx(
                      'px-3 py-1 text-xs font-medium rounded-md transition-colors',
                      actionFilter === option.value
                        ? 'bg-slate-800 text-white'
                        : 'text-slate-600 hover:bg-slate-100'
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <CardContent className="p-0">
              {filteredMaterials.length === 0 ? (
                <EmptyState
                  message={
                    materials.length === 0
                      ? 'Nenhuma necessidade de material calculada'
                      : 'Nenhum material corresponde ao filtro'
                  }
                />
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <tr>
                        <th className="text-left">Código</th>
                        <th className="text-left">Descrição</th>
                        <th className="text-left">Tipo</th>
                        <th className="text-left">Un.</th>
                        <th className="text-right">Necessidade bruta</th>
                        <th className="text-right">Estoque disponível</th>
                        <th className="text-right">Necessidade líquida</th>
                        <th className="text-center">Ação</th>
                        <th className="text-right">Estoque mín/máx</th>
                      </tr>
                    </TableHeader>
                    <tbody>
                      {filteredMaterials.map((material) => (
                        <TableRow key={material.raw_material_id}>
                          <TableCell>
                            <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">
                              {material.code}
                            </code>
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-slate-700">{material.description}</span>
                          </TableCell>
                          <TableCell>
                            <MaterialTypeBadge materialType={material.material_type} />
                          </TableCell>
                          <TableCell>
                            <span className="text-sm text-slate-500">{material.unit || '-'}</span>
                          </TableCell>
                          <TableCell className="text-right">
                            {formatNumber(material.gross_required)}
                          </TableCell>
                          <TableCell className="text-right">
                            {formatNumber(material.stock_available)}
                          </TableCell>
                          <TableCell className="text-right">
                            <span
                              className={clsx(
                                material.net_required > 0
                                  ? 'font-bold text-slate-900'
                                  : 'text-slate-400'
                              )}
                            >
                              {formatNumber(material.net_required)}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            {material.has_bom ? (
                              <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-purple-100 text-purple-800">
                                Produzir
                              </span>
                            ) : (
                              <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                                Comprar
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className="text-sm text-slate-500">
                              {formatNumber(material.min_stock_quantity)} /{' '}
                              {formatNumber(material.max_stock_quantity)}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

// ============================================================
// Aba: Custo por OP
// ============================================================

const OrderCostTab: React.FC = () => {
  const [dateFrom, setDateFrom] = useState(() => localISO(-30));
  const [dateTo, setDateTo] = useState(() => localISO());

  const [rows, setRows] = useState<OrderCostRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await productionService.getOrderCostReport({
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
      });
      setRows(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar custo por OP');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFrom, dateTo]);

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <Card>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 items-end">
            <PeriodFilterFields
              dateFrom={dateFrom}
              dateTo={dateTo}
              onDateFrom={setDateFrom}
              onDateTo={setDateTo}
            />
            <div>
              <Button
                variant="outline"
                onClick={loadData}
                loading={loading}
                icon={<RefreshCw size={16} />}
              >
                Atualizar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {error && <ErrorBanner message={error} />}

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <LoadingState message="Carregando custos..." />
          ) : rows.length === 0 ? (
            <EmptyState message="Nenhuma ordem de produção concluída no período" />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <tr>
                    <th className="text-left">Código OP</th>
                    <th className="text-left">Descrição</th>
                    <th className="text-left">Cor</th>
                    <th className="text-left">Concluída em</th>
                    <th className="text-right">Produzido</th>
                    <th className="text-right">Perdido</th>
                    <th className="text-right">% Perda</th>
                    <th className="text-right">Custo materiais</th>
                    <th className="text-right">Custo material/unid.</th>
                    <th className="text-right">Minutos totais</th>
                    <th className="text-right">Min/unid.</th>
                  </tr>
                </TableHeader>
                <tbody>
                  {rows.map((row) => (
                    <TableRow key={row.production_order_id}>
                      <TableCell>
                        <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">{row.code}</code>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-700">{row.description || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-500">{row.flock_color || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-700">
                          {formatDate(row.completed_at)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <span className="font-medium text-emerald-700">
                          {formatNumber(row.quantity_produced)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        {row.quantity_lost ? (
                          <span className="font-medium text-red-600">
                            {formatNumber(row.quantity_lost)}
                          </span>
                        ) : (
                          <span className="text-slate-400">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatPercent(row.loss_rate_percent)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(row.material_cost)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(row.material_cost_per_unit)}
                      </TableCell>
                      <TableCell className="text-right">
                        {row.labor_minutes ? (
                          formatMinutes(row.labor_minutes)
                        ) : (
                          <span className="text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatNumber(row.minutes_per_unit, 1)}
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================================
// Página principal
// ============================================================

/**
 * Relatórios de Produção — produtividade, balanço de materiais,
 * necessidade de compra e custo por Ordem de Produção
 */
export const ProductionReportsView: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-h1">Relatórios de Produção</h1>
        <p className="text-slate-600">
          Produtividade dos colaboradores, balanço de materiais, necessidade de compra e custo por
          OP
        </p>
      </div>

      <Tabs
        tabs={[
          {
            id: 'productivity',
            label: 'Produtividade',
            icon: <Users size={16} />,
            content: <ProductivityTab />,
          },
          {
            id: 'material-balance',
            label: 'Balanço de Materiais',
            icon: <Scale size={16} />,
            content: <MaterialBalanceTab />,
          },
          {
            id: 'material-requirements',
            label: 'Necessidade de Compra',
            icon: <ShoppingCart size={16} />,
            content: <MaterialRequirementsTab />,
          },
          {
            id: 'order-cost',
            label: 'Custo por OP',
            icon: <DollarSign size={16} />,
            content: <OrderCostTab />,
          },
        ]}
      />
    </div>
  );
};
